import React, { useState, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import set from 'lodash/set';

import TextField from '../../../shared/TextField';
import TextArea from '../../../shared/TextArea';
import AppContext from '../../../context/AppContext';
import Checkbox from '../../../shared/Checkbox';
import { addItem, deleteItem, moveItemUp, moveItemDown } from '../../../utils';
import ItemActions from '../../../shared/ItemActions';

const ReferencesTab = ({ data, onChange, theme }) => {
  const context = useContext(AppContext);
  const { dispatch } = context;

  useEffect(() => {
    if (!('references' in data)) {
      dispatch({
        type: 'migrate_section',
        payload: {
          key: 'references',
          value: {
            enable: false,
            heading: 'References',
            items: [],
          },
        },
      });

      dispatch({ type: 'save_data' });
    }
  }, [data, dispatch]);

  return (
    'references' in data && (
      <>
        <div className="mb-6 grid grid-cols-6 items-center">
          <div className="col-span-1">
            <Checkbox
              checked={data.references.enable}
              onChange={v => onChange('data.references.enable', v)}
              theme={theme}
            />
          </div>
          <div className="col-span-5">
            <TextField
              placeholder="Heading"
              value={data.references.heading}
              onChange={v => onChange('data.references.heading', v)}
              theme={theme}
            />
          </div>
        </div>

        <hr className={`my-6 ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`} />

        {data.references.items.map((x, index) => (
          <Item
            item={x}
            key={x.id}
            index={index}
            onChange={onChange}
            dispatch={dispatch}
            first={index === 0}
            last={index === data.references.items.length - 1}
            theme={theme}
          />
        ))}

        <AddItem dispatch={dispatch} theme={theme} />
      </>
    )
  );
};

const AddItem = ({ dispatch, theme }) => {
  const [isOpen, setOpen] = useState(false);
  const [item, setItem] = useState({
    id: uuidv4(),
    enable: true,
    name: '',
    position: '',
    phone: '',
    email: '',
    description: '',
  });

  const onChange = (key, value) => setItem(set({ ...item }, key, value));

  const onSubmit = () => {
    if (item.name === '' || item.position === '') return;

    addItem(dispatch, 'references', item);

    setItem({
      id: uuidv4(),
      enable: true,
      name: '',
      position: '',
      phone: '',
      email: '',
      description: '',
    });

    setOpen(false);
  };

  return (
    <div className={`my-4 border rounded p-5 ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
      <div
        className={`flex justify-between items-center cursor-pointer ${theme === 'light' ? 'text-gray-800' : 'text-gray-200'}`}
        onClick={() => setOpen(!isOpen)}
      >
        <h6 className="text-sm font-medium">Add Reference</h6>
        <i className="material-icons">{isOpen ? 'expand_less' : 'expand_more'}</i>
      </div>

      <div className={`mt-6 ${isOpen ? 'block' : 'hidden'}`}>
        <TextField
          label="Name"
          className="mb-6"
          placeholder="Steve Jobs"
          value={item.name}
          onChange={v => onChange('name', v)}
          theme={theme}
        />

        <TextField
          label="Position"
          className="mb-6"
          placeholder="CEO of Apple"
          value={item.position}
          onChange={v => onChange('position', v)}
          theme={theme}
        />

        <TextField
          label="Phone Number"
          className="mb-6"
          placeholder="+1 123 456 7890"
          value={item.phone}
          onChange={v => onChange('phone', v)}
          theme={theme}
        />

        <TextField
          label="Email Address"
          className="mb-6"
          placeholder="steve@apple.com"
          value={item.email}
          onChange={v => onChange('email', v)}
          theme={theme}
        />

        <TextArea
          rows="5"
          className="mb-6"
          label="Description"
          placeholder="You can write about how you and the reference contact worked together and which projects you were a part of."
          value={item.description}
          onChange={v => onChange('description', v)}
          theme={theme}
        />

        <button
          type="button"
          onClick={onSubmit}
          className="bg-gray-600 hover:bg-gray-700 text-white btn btn-primary"
        >
          <div className="flex items-center">
            <i className="material-icons mr-2 font-bold text-base">add</i>
            <span className="text-sm">Add</span>
          </div>
        </button>
      </div>
    </div>
  );
};

const Item = ({ item, index, onChange, dispatch, first, last, theme }) => {
  const [isOpen, setOpen] = useState(false);
  const identifier = `data.references.items[${index}]`;

  return (
    <div className={`my-4 border rounded p-5 ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
      <div
        className={`flex justify-between items-center cursor-pointer ${theme === 'light' ? 'text-gray-800' : 'text-gray-200'}`}
        onClick={() => setOpen(!isOpen)}
      >
        <h6 className="text-sm font-medium">{item.name}</h6>
        <i className="material-icons">{isOpen ? 'expand_less' : 'expand_more'}</i>
      </div>

      <div className={`mt-6 ${isOpen ? 'block' : 'hidden'}`}>
        <TextField
          label="Name"
          className="mb-6"
          placeholder="Steve Jobs"
          value={item.name}
          onChange={v => onChange(`${identifier}.name`, v)}
          theme={theme}
        />

        <TextField
          label="Position"
          className="mb-6"
          placeholder="CEO of Apple"
          value={item.position}
          onChange={v => onChange(`${identifier}.position`, v)}
          theme={theme}
        />

        <TextField
          label="Phone Number"
          className="mb-6"
          placeholder="+1 123 456 7890"
          value={item.phone}
          onChange={v => onChange(`${identifier}.phone`, v)}
          theme={theme}
        />

        <TextField
          label="Email Address"
          className="mb-6"
          placeholder="steve@apple.com"
          value={item.email}
          onChange={v => onChange(`${identifier}.email`, v)}
          theme={theme}
        />

        <TextArea
          rows="5"
          className="mb-6"
          label="Description"
          placeholder="You can write about how you and the reference contact worked together and which projects you were a part of."
          value={item.description}
          onChange={v => onChange(`${identifier}.description`, v)}
          theme={theme}
        />

        <ItemActions
          item={item}
          onChange={onChange}
          type="references"
          identifier={identifier}
          dispatch={dispatch}
          deleteItem={deleteItem}
          first={first}
          moveItemUp={moveItemUp}
          last={last}
          moveItemDown={moveItemDown}
          theme={theme}
        />
      </div>
    </div>
  );
};

export default ReferencesTab;
